import React, { ChangeEvent, FC, useEffect, useState } from 'react';
import { Box, Button, FormControl, Grid, TextField } from '@mui/material';
import { MaterialMacro } from 'models/material-macro';
import { useMutation } from '@apollo/client';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { modifyMaterialMacroTemplate } from 'apollo/templates/material-macro';
import { useTranslation } from 'react-i18next';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';

type EditMaterialMacroDialogProps = {
    materialMacro: MaterialMacro;
    dialogOpen: boolean;
    onCloseDialog: () => void;
    onEditMaterialMacro: (materialMacro: MaterialMacro) => void;
    materialMacros: MaterialMacro[];
};
const EditMaterialMacroDialog: FC<EditMaterialMacroDialogProps> = ({
    materialMacro,
    dialogOpen,
    onCloseDialog,
    onEditMaterialMacro,
    materialMacros,
}: EditMaterialMacroDialogProps) => {
    const { t: translate } = useTranslation(['lab-settings', 'workflow']);
    const [materialMacroName, setMaterialMacroName] = useState('');
    const [materialMacroNameError, setMaterialMacroNameError] = useState(false);
    const { showBoundary } = useDenthubErrorBoundary();

    const [modifyMaterialMacro] = useMutation(modifyMaterialMacroTemplate, {
        variables: {
            _id: materialMacro._id,
            name: materialMacroName,
        },
        onCompleted: ({ modifyMaterialMacro }) => {
            onEditMaterialMacro(modifyMaterialMacro);
            onCloseDialog();
        },
        onError(error) {
            showBoundary(error);
        },
    });

    useEffect(() => {
        if (materialMacro) {
            setMaterialMacroName(materialMacro.name);
        }
    }, [materialMacro]);

    const handleMaterialMacroNameChange = (event: ChangeEvent<HTMLInputElement>) => {
        setMaterialMacroNameError(false);
        if (!event.target.value || materialMacroNameAlreadyExist(event.target.value)) {
            setMaterialMacroNameError(true);
        }
        setMaterialMacroName(event.target.value);
    };

    const materialMacroNameAlreadyExist = (name: string) => {
        const macrosWithoutCurrent = materialMacros.filter((macro) => macro._id !== materialMacro._id);
        const materialMacroNames = macrosWithoutCurrent.map((macro) =>
            macro.name.replace(/\s/g, '').toLocaleLowerCase(),
        );
        return materialMacroNames.includes(name.replace(/\s/g, '').toLocaleLowerCase());
    };

    return (
        <DHDialog
            fullWidth
            maxWidth="sm"
            open={dialogOpen}
            onClose={onCloseDialog}
            title={translate('lab-settings:EDIT_MATERIAL_MACRO')}
            actions={
                <Button variant="contained" disabled={materialMacroNameError} onClick={() => modifyMaterialMacro()}>
                    {translate('lab-settings:SAVE_MATERIAL_MACRO')}
                </Button>
            }
        >
            <Box mt={1} mb={2}>
                <Grid container spacing={2}>
                    <Grid item md={8} sm={12}>
                        <FormControl fullWidth margin="dense">
                            <TextField
                                id="material-macro-name"
                                label={translate('lab-settings:MATERIAL_MACRO_NAME')}
                                required
                                defaultValue={materialMacroName}
                                onChange={handleMaterialMacroNameChange}
                                error={materialMacroNameError}
                                helperText={
                                    materialMacroNameError && !materialMacroName
                                        ? translate('workflow:VALIDATION:NAME')
                                        : materialMacroNameError &&
                                          translate('lab-settings:VALIDATION:MATERIAL_MACRO_ALREADY_EXIST')
                                }
                            />
                        </FormControl>
                    </Grid>
                </Grid>
            </Box>
        </DHDialog>
    );
};

export default EditMaterialMacroDialog;
